import {
    clampFixedPosition,
    clampVisibleSize,
    getLayoutViewportSize,
    getVisibleViewportSize
} from "../../common/components/FloatingPanel/floatingPanelViewport";

const SAMPLE_TEXT_VIEWER_DEFAULT_SIZE = { width: 480, height: 420 };
const VIEWPORT_MARGIN = 16;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 240;
const MAX_WIDTH = 900;

function clampSampleTextViewerSize(size) {
    return clampVisibleSize(
        size ?? {},
        {
            minWidth: MIN_WIDTH,
            minHeight: MIN_HEIGHT,
            maxWidth: MAX_WIDTH,
            maxHeight: Math.round(getVisibleViewportSize().height * 0.8)
        },
        VIEWPORT_MARGIN
    );
}

function fitSampleTextViewerOpenLayout(position, size) {
    const fittedSize = clampSampleTextViewerSize(size);
    return {
        size: fittedSize,
        position: clampFixedPosition(position, fittedSize, VIEWPORT_MARGIN)
    };
}

function fallbackLayout() {
    const size = clampSampleTextViewerSize(SAMPLE_TEXT_VIEWER_DEFAULT_SIZE);
    const { width: viewportW } = getLayoutViewportSize();
    return fitSampleTextViewerOpenLayout(
        {
            top: VIEWPORT_MARGIN + 56,
            left: Math.max(VIEWPORT_MARGIN, viewportW - size.width - VIEWPORT_MARGIN)
        },
        size
    );
}

export function measureSampleTextViewerBandDefaultSize(row) {
    const rect = row.getBoundingClientRect();
    return clampSampleTextViewerSize({
        width: Math.max(SAMPLE_TEXT_VIEWER_DEFAULT_SIZE.width, Math.round(rect.width)),
        height: SAMPLE_TEXT_VIEWER_DEFAULT_SIZE.height
    });
}

export function computeSampleTextViewerLayoutForBandRow(rowKey, getBandRowDomId) {
    const row = document.getElementById(getBandRowDomId(rowKey));
    if (!row) return fallbackLayout();

    const rect = row.getBoundingClientRect();
    const barRect = row.querySelector(".exec-sum-band-row__bar")?.getBoundingClientRect?.() ?? rect;
    const size = measureSampleTextViewerBandDefaultSize(row);
    return fitSampleTextViewerOpenLayout(
        { top: Math.round(barRect.top), left: Math.round(rect.left) },
        size
    );
}

export function computeSampleTextViewerLayoutBelowPanelHeader(panelEl) {
    if (!panelEl) return fallbackLayout();
    const panelRect = panelEl.getBoundingClientRect();
    const headerRect = panelEl.querySelector(".floating-panel__header")?.getBoundingClientRect?.() ?? panelRect;
    const gap = 8;
    const horizontalInset = Math.max(12, Math.round(panelRect.width * 0.04));
    const targetWidth = Math.min(Math.round(panelRect.width - horizontalInset * 2), panelRect.width);
    const availableHeight = Math.max(280, panelRect.bottom - headerRect.bottom - gap - VIEWPORT_MARGIN);
    const targetHeight = Math.min(420, availableHeight);
    const size = clampSampleTextViewerSize({
        width: Math.max(SAMPLE_TEXT_VIEWER_DEFAULT_SIZE.width, targetWidth),
        height: Math.max(SAMPLE_TEXT_VIEWER_DEFAULT_SIZE.height, targetHeight)
    });
    const centeredLeft = panelRect.left + (panelRect.width - size.width) / 2;
    const top = headerRect.bottom + gap;
    const minLeft = panelRect.left + horizontalInset;
    const maxLeft = panelRect.right - size.width - horizontalInset;
    const left = Math.max(minLeft, Math.min(centeredLeft, maxLeft));
    return fitSampleTextViewerOpenLayout({ top: Math.round(top), left: Math.round(left) }, size);
}

const EXEC_SUM_TEXT_BAND_EDITOR_MIN = { width: 520, height: 400 };
const EXEC_SUM_TEXT_BAND_EDITOR_MARGIN = 24;

export function computeExecSummaryTextBandEditorLayout() {
    const { width: visibleW, height: visibleH } = getVisibleViewportSize();
    const margin = EXEC_SUM_TEXT_BAND_EDITOR_MARGIN;
    const width = Math.min(1140, Math.max(EXEC_SUM_TEXT_BAND_EDITOR_MIN.width, visibleW - margin * 2));
    const height = Math.max(
        EXEC_SUM_TEXT_BAND_EDITOR_MIN.height,
        Math.min(visibleH - margin * 2, visibleH - 80)
    );
    return {
        size: {
            width: Math.round(width),
            height: Math.round(height)
        }
    };
}

export function computeExecSummaryTextBandEditorMaxSize() {
    const { width: visibleW, height: visibleH } = getVisibleViewportSize();
    return {
        width: Math.min(1140, visibleW - VIEWPORT_MARGIN * 2),
        height: visibleH - VIEWPORT_MARGIN * 2
    };
}

export { SAMPLE_TEXT_VIEWER_DEFAULT_SIZE };
